import { z } from 'zod';

import {
  isScaleQuestion,
  isScaleResponse,
  QuestionResponse,
  TypedQuestion,
  ScaleQuestion,
  ScaleQuestionResponse
} from 'types/question-response.types';

import { IsRequired, QuestionResponseFormValue } from '../base.types';

type BaseScaleQuestionResponseFormValue = QuestionResponseFormValue<ScaleQuestionResponse, 'value'>;

type NonEmptyScaleQuestionResponseFormValue = BaseScaleQuestionResponseFormValue & {
  value: string;
};
export type FormValue = BaseScaleQuestionResponseFormValue | NonEmptyScaleQuestionResponseFormValue;

export const isNonEmpty = (
  response: FormValue
): response is NonEmptyScaleQuestionResponseFormValue => {
  const { value } = response;
  return value !== undefined;
};

export const submissionValue = ({
  type,
  value
}: NonEmptyScaleQuestionResponseFormValue): Omit<ScaleQuestionResponse, 'id'> => {
  return { type, value };
};

export const schema = ({ type }: ScaleQuestion): z.ZodType<FormValue> => {
  const baseSchema = z.object({ type: z.literal(type) });
  const valueSchema = z.discriminatedUnion('req', [
    z.object({
      value: z.string().nonempty('This question is required.'),
      req: z.literal(IsRequired.Req)
    }),
    z.object({
      value: z.string().optional(),
      req: z.literal(IsRequired.Opt)
    })
  ]);
  return baseSchema.and(valueSchema);
};

export const initialize: (
  question: ScaleQuestion,
  response?: ScaleQuestionResponse
) => FormValue = ({ type, isRequired: required }, response) => {
  const req = required ? IsRequired.Req : IsRequired.Opt;
  const value = response?.value;
  return { type, req, value };
};

export const validateAndInitialize = (
  question: TypedQuestion,
  response?: QuestionResponse
): FormValue | undefined => {
  if (!isScaleQuestion(question)) return undefined;
  if (response && isScaleResponse(response)) return initialize(question, response);
  return initialize(question);
};

export const validateAndSchematize = (
  question: TypedQuestion
): z.ZodType<FormValue> | undefined => {
  return isScaleQuestion(question) ? schema(question) : undefined;
};

export const validateAndSerialize = (
  id: number,
  formValue: FormValue
): QuestionResponse | undefined => {
  if (!isNonEmpty(formValue)) return undefined;
  return { id, ...submissionValue(formValue) };
};
